// @flow

import React, {useEffect, useState} from 'react';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import CustomButton from '../../../../../elements/CustomButton/CustomButton';
import {
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
  makeStyles,
} from '@material-ui/core';
import {colors} from '../../../../../assets/styles/colors';

import type {
  ProductVariantOption,
  ProductVariantOptionValue,
} from '../../../../../entities';

import {getImageUrl} from '../../../../../utils/image_provider';
import {getProductVariantOptionService} from '../../../promises';

type Props = {
  open: boolean,
  handleClose: () => void,
  onConfirm: (selectedValues: ProductVariantOptionValue[]) => void,
  variantOption: ProductVariantOption,
  selectedOptions: ProductVariantOptionValue[],
};

const useStyles = makeStyles(theme => ({
  btn: {
    color: colors.grey800,
    borderRadius: 10,
    background: colors.grey200,
  },
  modal: {
    width: 460,
    maxWidth: '90%',
    maxHeight: '80%',
  },
  title: {
    color: '#313135',
    fontSize: 20,
    textAlign: 'center',
    fontWeight: 600,
  },
  content: {
    margin: theme.spacing(1.5, 0),
    padding: theme.spacing(2),
    border: `1px solid ${colors.grey300}`,
    borderRadius: 12,
    maxHeight: 600,
    overflow: 'auto',
  },
  valueName: {
    fontSize: 14,
    color: colors.grey900,
  },
  img: {
    maxWidth: '100%',
    maxHeight: '100%',
    width: '100%',
    height: '100%',
  },
  value: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
}));

const buildInitialState = (options: ProductVariantOptionValue[]) => {
  return options.map(el => ({...el, enabled: false}));
};
export type EditOptionValue = ProductVariantOptionValue & {
  enabled: boolean,
};

const SelectVariantOptionModal = ({
  variantOption,
  open,
  onConfirm,
  handleClose,
  selectedOptions,
}: Props) => {
  const [optionValues, setOptionValues] = useState<EditOptionValue[]>(
    buildInitialState(variantOption.values ?? [])
  );
  const classes = useStyles();

  const buildValuePreview = (value: ProductVariantOptionValue) => {
    const isColor = variantOption.displayType === 'COLOR';
    const isImage = variantOption.imageShape === 'IMAGE';

    const style = {
      width: 24,
      height: 24,
      borderRadius:
        variantOption.imageShape === 'CIRCULAR' ? '100%' : undefined,
      backgroundColor: isColor ? value.color : undefined,
    };

    return (
      <div style={style}>
        {isImage && (
          <img
            className={classes.img}
            src={getImageUrl(value.imageId)}
            alt=""
          />
        )}
      </div>
    );
  };

  const handleEnableValue = (enabled: boolean, index: number) => {
    const aux = [...optionValues];
    aux[index].enabled = enabled;

    setOptionValues(aux);
  };

  const handleConfirm = () => {
    onConfirm(
      optionValues.filter(v => v.enabled).map(({enabled, ...rest}) => rest)
    );
  };

  useEffect(() => {
    if (variantOption) {
      getProductVariantOptionService(variantOption.id)
        .then(el => {
          if (!el) return;
          const optValues = el.values.map(el => ({
            ...el,
            enabled: !!selectedOptions.find(s => s.id === el.id),
          }));

          setOptionValues(optValues);
        })
        .catch(err => null);
    }
  }, [selectedOptions, variantOption]);

  return (
    <CustomModal open={open} handleCloseModal={handleClose}>
      <div className={classes.modal}>
        <Typography className={classes.title}>
          Variantes de {variantOption.label}
        </Typography>

        <div className={classes.content}>
          <Grid container spacing={2}>
            {optionValues.map((el, idx) => (
              <Grid
                item
                xs={12}
                md={6}
                key={el.id}
                className={classes.gridContent}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={el.enabled}
                      onChange={e => handleEnableValue(e.target.checked, idx)}
                    />
                  }
                  labelPlacement="end"
                  label={
                    <div className={classes.value}>
                      {buildValuePreview(el)}
                      <Typography className={classes.valueName}>
                        {el.label}
                      </Typography>
                    </div>
                  }
                />
              </Grid>
            ))}
          </Grid>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CustomButton
            variant="contained"
            style={{width: '15rem'}}
            onClick={handleConfirm}
            disabled={optionValues.filter(el => el.enabled).length === 0}
          >
            Confirmar
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default SelectVariantOptionModal;
